import React, { useState } from 'react';
import {
  TextField,
  Button,
  Typography,
  CircularProgress,
  Box,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import Navbar from '../../globals/components/Navbar';
import { apiClient } from '../../api/httpClient';
import { toast } from 'react-toastify';
import { updateUsername } from '../../store/slices/authSlice'; // Import the action
import debounce from "lodash.debounce";
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import { EMAIL_DEBOUNCE_TIMEOUT } from "../../globals/utils/constants";

const UserProfileUpdate = () => {
  const { handleSubmit, control, setValue } = useForm();
  const [loading, setLoading] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  // Redux dispatch for state updates
  const dispatch = useDispatch();

  // Safely fetch user details from Redux
  const userdetails = useSelector((state) => state.auth?.user);
  const currentUsername = userdetails?.username;
  const currentFirstName = userdetails?.first_name;
  const currentLastName = userdetails?.last_name;
  const currentEmail = userdetails?.email;

  // Debounced email check states
  const [emailExists, setEmailExists] = useState(false);
  const [emailChecking, setEmailChecking] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);

  // Debounced function to check email existence
  const debouncedCheckEmail = React.useRef(
    debounce(async (email) => {
      if (!email) {
        setEmailExists(false);
        setEmailChecking(false);
        setEmailDialogOpen(false);
        return;
      }
      setEmailChecking(true);
      try {
        await apiClient("/email-exists", "POST", { email });
        setEmailExists(false);
      } catch (e) {
        if (e?.detail === "Email already exists") {
          setEmailDialogOpen(true);
          setEmailExists(true);
        } else {
          setEmailExists(false);
        }
      } finally {
        setEmailChecking(false);
      }
    }, EMAIL_DEBOUNCE_TIMEOUT)
  ).current;

  // Handler for new email field
  const handleNewEmailChange = (e, fieldOnChange) => {
    fieldOnChange(e);
    debouncedCheckEmail(e.target.value);
  };

  // Dialog handlers
  const handleDialogClose = () => {
    setEmailDialogOpen(false);
  };
  const handleDialogContinue = () => {
    setEmailDialogOpen(false);
    setEmailExists(false);
    // Allow user to proceed
  };
  const handleDialogCancel = () => {
    setEmailDialogOpen(false);
    setEmailExists(false);
    setValue("newEmail", "");
  };

  const updateUserDetails = async (data) => {
    if (!userdetails) {
      toast.error('User details not available. Please log in.');
      return;
    }

    const sensitiveChange =
      Boolean(data.newEmail?.trim()) ||
      Boolean(data.newUsername?.trim()) ||
      Boolean(data.newPassword);

    if (sensitiveChange && !data.oldPassword) {
      toast.error(
        'Enter your current password to change email, username, or password.'
      );
      return;
    }

    setLoading(true);
    try {
      const requestData = {
        username: currentUsername,
        old_password: data.oldPassword || undefined,
        new_password: data.newPassword || undefined,
        new_username: data.newUsername || undefined,
        first_name: data.firstName || undefined,
        last_name: data.lastName || undefined,
        new_email: data.newEmail || undefined,
      };

      // Remove undefined fields to avoid unnecessary API issues
      Object.keys(requestData).forEach(
        (key) => requestData[key] === undefined && delete requestData[key]
      );

      const response = await apiClient('/update-user-details', 'POST', requestData);

      // Show success message
      toast.success(response.detail || 'User details updated successfully!');

      // Dispatch updateUsername action if username was updated
      if (data.newUsername) {
        dispatch(updateUsername({ username: data.newUsername }));
      }

      // Update first_name and last_name in the Redux store if required
      if (data.firstName || data.lastName) {
        dispatch(updateUsername({ 
          first_name: data.firstName || currentFirstName, 
          last_name: data.lastName || currentLastName 
        }));
      }

      // Update email in Redux if changed
      if (data.newEmail) {
        dispatch(require('../../store/slices/authSlice').updateEmail({ email: data.newEmail }));
      }
    } catch (error) {
      toast.error(error.detail || 'Failed to update user details.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar />
      <Box className="max-w-3xl mx-auto mt-8 mb-8 px-4">
        {/* Header */}
        <Box className="bg-gray-100 p-6 rounded-lg shadow-md mb-6" sx={{ border: "1px solid #e0e0e0" }}>
          <Typography variant="h5" className="font-bold mb-2">
            Update Your Profile
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Keep your information up to date to ensure the best experience
          </Typography>
        </Box>

        {userdetails ? (
          <form onSubmit={handleSubmit(updateUserDetails)}>
            {/* Personal Information Section */}
            <Box className="bg-gray-100 p-6 rounded-lg shadow-md mb-6" sx={{ border: "1px solid #e0e0e0" }}>
              <Typography variant="h5" className="font-bold mb-4">
                Personal Information
              </Typography>
              <Box className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Controller
                  name="firstName"
                  control={control}
                  defaultValue={currentFirstName || ""}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="First Name"
                      fullWidth
                      variant="outlined"
                    />
                  )}
                />
                <Controller
                  name="lastName"
                  control={control}
                  defaultValue={currentLastName || ""}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Last Name"
                      fullWidth
                      variant="outlined"
                    />
                  )}
                />
              </Box>
            </Box>

            {/* Account Information Section */}
            <Box className="bg-gray-100 p-6 rounded-lg shadow-md mb-6" sx={{ border: "1px solid #e0e0e0" }}>
              <Typography variant="h5" className="font-bold mb-4">
                Account Information
              </Typography>
              <Box className="grid grid-cols-1 gap-4">
                <TextField
                  label="Current Username"
                  value={currentUsername}
                  fullWidth
                  variant="outlined"
                  disabled
                />
                <Controller
                  name="newUsername"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="New Username (optional)"
                      fullWidth
                      variant="outlined"
                    />
                  )}
                />
              </Box>
            </Box>

            {/* Email Section */}
            <Box className="bg-gray-100 p-6 rounded-lg shadow-md mb-6" sx={{ border: "1px solid #e0e0e0" }}>
              <Typography variant="h5" className="font-bold mb-4">
                Email Address
              </Typography>
              <Box className="grid grid-cols-1 gap-4">
                <TextField
                  label="Current Email"
                  value={currentEmail}
                  fullWidth
                  variant="outlined"
                  disabled
                />
                <Controller
                  name="newEmail"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <>
                      <TextField
                        {...field}
                        label="New Email (optional)"
                        type="email"
                        fullWidth
                        variant="outlined"
                        error={Boolean(emailExists)}
                        helperText={
                          emailChecking
                            ? "Checking email availability..."
                            : emailExists
                            ? "This email already exists."
                            : ""
                        }
                        onChange={e => handleNewEmailChange(e, field.onChange)}
                        slotProps={{ input: {
                          endAdornment: emailChecking && (
                            <InputAdornment position="end">
                              <CircularProgress size={20} />
                            </InputAdornment>
                          ),
                        }}}
                      />
                      <Dialog
                        open={emailDialogOpen}
                        onClose={(_, reason) => {
                          if (reason !== 'backdropClick') handleDialogClose();
                        }}
                      >
                        <DialogTitle>
                          Email Already Exists
                        </DialogTitle>
                        <DialogContent>
                          <DialogContentText>
                            This email already exists in our system. Would you like to continue with this email address?
                          </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                          <Button 
                            onClick={handleDialogCancel} 
                            variant="outlined"
                          >
                            Cancel
                          </Button>
                          <Button 
                            onClick={handleDialogContinue} 
                            variant="contained"
                            autoFocus
                          >
                            Continue
                          </Button>
                        </DialogActions>
                      </Dialog>
                    </>
                  )}
                />
              </Box>
            </Box>

            {/* Security Section */}
            <Box className="bg-gray-100 p-6 rounded-lg shadow-md mb-6" sx={{ border: "1px solid #e0e0e0" }}>
              <Typography variant="h5" className="font-bold mb-4">
                Security Settings
              </Typography>
              <Box className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Controller
                  name="oldPassword"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Current Password"
                      helperText="Required when changing email, username, or password"
                      type={showOldPassword ? "text" : "password"}
                      fullWidth
                      variant="outlined"
                      slotProps={{ input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowOldPassword(!showOldPassword)}
                              edge="end"
                              size="small"
                            >
                              {showOldPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}}
                    />
                  )}
                />
                <Controller
                  name="newPassword"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="New Password"
                      type={showNewPassword ? "text" : "password"}
                      fullWidth
                      variant="outlined"
                      slotProps={{ input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowNewPassword(!showNewPassword)}
                              edge="end"
                              size="small"
                            >
                              {showNewPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}}
                    />
                  )}
                />
              </Box>
            </Box>

            {/* Submit Button */}
            <Box className="text-center mt-6">
              <Button 
                type="submit" 
                variant="contained" 
                size="large"
                disabled={loading}
                startIcon={loading && <CircularProgress size={20} color="inherit" />}
              >
                {loading ? "Updating Profile..." : "Update Profile"}
              </Button>
            </Box>
          </form>
        ) : (
          <Box className="bg-gray-100 p-6 rounded-lg shadow-md" sx={{ border: "1px solid #e0e0e0" }}>
            <Typography variant="h6" className="font-bold mb-2" color="error">
              Authentication Required
            </Typography>
            <Typography variant="body1">
              User details not available. Please log in to update your profile.
            </Typography>
          </Box>
        )}
      </Box>
    </>
  );
};

export default UserProfileUpdate;
